import React, { useState, useEffect, useCallback } from 'react';
import {
  Settings, Mail, Shield, Save, Loader2, CheckCircle, AlertTriangle,
  Globe, Users, FileText, KeyRound
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/lib/api';
import { EmailTab } from './platformSettings/EmailTab';
import { BrandingTab } from './platformSettings/BrandingTab';
import { FeaturesTab } from './platformSettings/FeaturesTab';
import { LimitsTab } from './platformSettings/LimitsTab';
import { CognisegTab } from './platformSettings/CognisegTab';
import { TemplatesTab } from './platformSettings/TemplatesTab';
import { SecurityTab } from './platformSettings/SecurityTab';

const PlatformSettingsPage = () => {
  const [settings, setSettings] = useState({
    sendgrid_api_key: '',
    sendgrid_webhook_public_key: '',
    sender_email: 'noreply@cognietica.pt',
    sender_name: 'CogniEtica',
    platform_name: 'CogniEtica',
    platform_logo_url: '',
    support_email: 'suporte@cognietica.pt',
    allow_tenant_custom_email: false,
    alert_delivery_failures: true,
    delivery_digest_enabled: false,
    delivery_digest_hour: 8,
    enable_ai_triage: true,
    enable_mfa: true,
    default_max_users: 5,
    default_max_reports_month: 50,
    public_base_url: 'https://cognietica.pt',
    cogniseg_triage_email: 'gestortriagem@cogniseg.com',
    cogniseg_triage_name: 'Gestor de Triagem',
    cogniseg_triage_password: '',
    has_cogniseg_password: false,
    default_privacy_policy_content: '',
    default_privacy_policy_url: '',
    default_terms_of_use_content: '',
    default_terms_of_use_url: ''
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showApiKey, setShowApiKey] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const response = await api.get('/super-admin/platform-settings');
      if (response.data) {
        setSettings(prev => ({ ...prev, ...response.data }));
      }
    } catch (err) {
      // Settings may not exist yet, use defaults
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      // Never send the plaintext CogniSeg password if the field is empty.
      const payload = { ...settings };
      if (!payload.cogniseg_triage_password) {
        delete payload.cogniseg_triage_password;
      }
      delete payload.has_cogniseg_password;
      delete payload.sendgrid_api_key_masked;
      await api.put('/super-admin/platform-settings', payload);
      setSuccess('Configurações guardadas com sucesso');
      // Clear the plaintext password from state after a successful save
      setSettings(prev => ({
        ...prev,
        cogniseg_triage_password: '',
        has_cogniseg_password: prev.has_cogniseg_password || !!payload.cogniseg_triage_password_hash || !!settings.cogniseg_triage_password,
      }));
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao guardar configurações');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings(prev => ({ ...prev, [field]: value }));
  };

  const handleSendTestEmail = async () => {
    if (!testEmail) { toast.error('Introduza um email de destino'); return; }
    setSendingTest(true);
    try {
      const res = await api.post('/super-admin/email/test', { to: testEmail });
      toast.success(`Email de teste enviado para ${res.data.to}. Verifique o Registo de Emails para o estado de entrega.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Não foi possível enviar o email de teste');
    } finally {
      setSendingTest(false);
    }
  };

  const handleSendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await api.post('/super-admin/email-digest/send-now');
      if (res.data?.sent) {
        toast.success(`Resumo enviado para ${res.data.recipients} Super Admin(s) — ${res.data.channels} canal(is) com problemas.`);
      } else {
        const reasons = {
          'no problem channels': 'Sem canais com problemas nas últimas 24h — nada a enviar.',
          'no sendgrid key': 'Configure primeiro a chave SendGrid.',
          'no super admins': 'Não há Super Admins ativos.',
        };
        toast.info(reasons[res.data?.reason] || 'Nada a enviar.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar o resumo');
    } finally {
      setSendingDigest(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-settings-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Settings className="w-6 h-6 text-indigo-700" />
          Configurações da Plataforma
        </h1>
        <p className="text-slate-500 mt-1">
          Configurações globais que afetam todos os clientes (tenants)
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="bg-red-900/50 border-red-500/50">
          <AlertTriangle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert className="bg-green-900/50 border-green-500/50">
          <CheckCircle className="w-4 h-4 text-green-500" />
          <AlertDescription className="text-green-600">{success}</AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="email" className="space-y-6">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="email" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger value="branding" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
            <Globe className="w-4 h-4 mr-2" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="features" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
            <Shield className="w-4 h-4 mr-2" />
            Funcionalidades
          </TabsTrigger>
          <TabsTrigger value="limits" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700">
            <Users className="w-4 h-4 mr-2" />
            Limites
          </TabsTrigger>
          <TabsTrigger value="cogniseg" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700" data-testid="tab-cogniseg">
            <Shield className="w-4 h-4 mr-2" />
            CogniSeg
          </TabsTrigger>
          <TabsTrigger value="templates" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700" data-testid="tab-templates">
            <FileText className="w-4 h-4 mr-2" />
            Templates Legais
          </TabsTrigger>
          <TabsTrigger value="security" className="data-[state=active]:bg-indigo-100 data-[state=active]:text-indigo-700" data-testid="tab-security">
            <KeyRound className="w-4 h-4 mr-2" />
            Segurança
          </TabsTrigger>
        </TabsList>

        <TabsContent value="email">
          <EmailTab
            settings={settings}
            handleChange={handleChange}
            showApiKey={showApiKey}
            setShowApiKey={setShowApiKey}
            sendingDigest={sendingDigest}
            onSendDigestNow={handleSendDigestNow}
            testEmail={testEmail}
            setTestEmail={setTestEmail}
            sendingTest={sendingTest}
            onSendTestEmail={handleSendTestEmail}
          />
        </TabsContent>

        <TabsContent value="branding">
          <BrandingTab settings={settings} handleChange={handleChange} />
        </TabsContent>

        <TabsContent value="features">
          <FeaturesTab settings={settings} handleChange={handleChange} />
        </TabsContent>

        <TabsContent value="limits">
          <LimitsTab settings={settings} handleChange={handleChange} />
        </TabsContent>

        <TabsContent value="cogniseg">
          <CognisegTab settings={settings} handleChange={handleChange} />
        </TabsContent>

        <TabsContent value="templates">
          <TemplatesTab settings={settings} handleChange={handleChange} />
        </TabsContent>

        <TabsContent value="security">
          <SecurityTab />
        </TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-indigo-700 hover:bg-amber-600 text-black"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Guardar Configurações
        </Button>
      </div>
    </div>
  );
};

export default PlatformSettingsPage;
